// ============================================
// EDIT MODAL - BOOK VECTORIZATION
// Atualizado:
// - Layout visual corrigido (Loading Overlay)
// - Evita "janela branca" durante o upload
// ============================================
import React, { Fragment, Component } from 'react'
import PropTypes from 'prop-types'
import Button from '@material-ui/core/Button'
import Dialog from '../../components/IdentifiedDialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import withMobileDialog from '@material-ui/core/withMobileDialog'
import Input from '@material-ui/core/Input'
import InputLabel from '@material-ui/core/InputLabel'
import FormControl from '@material-ui/core/FormControl'
import TextField from '@material-ui/core/TextField'
import { connect } from 'react-redux'
import { bookVectorizationActions, messageActions } from '../../actions'
import { withStyles } from '@material-ui/core/styles'
import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'
import SelectWrapped from '../../components/SelectWrapped'
import { formatContentSelectValue, filterOptionsFunction, getApiErrorMessage } from '../../utils'
import { readOnly } from '../../utils/permissions'
import LinearProgress from '@material-ui/core/LinearProgress'
import CloudUpload from '@material-ui/icons/CloudUpload'
import InsertDriveFile from '@material-ui/icons/InsertDriveFile'
import Chip from '@material-ui/core/Chip'
import * as API from '../../api'
import { STATUS_CONFIG } from '../../constants/vectorization'
import { getBookCmsPlan, saveBookCmsPlan } from '../../utils/bookVectorizationCmsPlan'
import { createFormData as createPreRegistrationFormData } from '../../utils/formatPreRegistrationToSend'
import BookVectorizationFlow from '../../components/BookVectorizationFlow'

var ALLOWED_EXTENSIONS = ['.epub', '.pdf']
var ALLOWED_MIMES = ['application/epub+zip', 'application/pdf']
var ACCEPT_ATTR = ALLOWED_EXTENSIONS.concat(ALLOWED_MIMES).join(',')
var ACCEPTED_LABEL = 'EPUB ou PDF'

const styles = (theme) => ({
  // Estilo do conteúdo do modal para permitir posicionamento do loader
  dialogContent: {
    position: 'relative',
    minHeight: '300px', // Garante altura mínima para não colapsar
  },
  // Overlay que fica por cima do formulário carregando
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.85)', // Fundo branco semi-transparente
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(2px)', // Efeito de desfoque moderno (se o browser suportar)
  },
  formControl: {
    margin: '14px 0',
  },
  errorMessage: {
    position: 'absolute',
    bottom: '-22px',
    left: 0,
  },
  uploadArea: {
    border: '2px dashed #ccc',
    borderRadius: '8px',
    padding: '24px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: '#1976d2',
      backgroundColor: '#f5f5f5',
    },
    '&:focus': {
      borderColor: '#1976d2',
      outline: '2px solid #1976d2',
      outlineOffset: '2px',
    },
  },
  uploadAreaActive: {
    borderColor: '#1976d2',
    backgroundColor: '#eef5fd',
  },
  uploadInput: {
    display: 'none',
  },
  uploadIcon: {
    fontSize: 48,
    color: '#999',
    marginBottom: '8px',
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '12px',
  },
  fileDetails: {
    marginLeft: '8px',
    minWidth: 0,
    textAlign: 'left',
  },
  fileActions: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginTop: '12px',
  },
  fileActionButton: {
    margin: '4px',
  },
  statusChip: {
    marginTop: '12px',
  },
  fieldset: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    margin: '12px 0 18px',
    padding: '10px 14px 14px',
  },
  warningBox: {
    backgroundColor: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: 4,
    padding: '10px 12px',
    margin: '12px 0',
  },
  legend: {
    padding: '0 6px',
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: '0.85rem',
  },
  syncSummary: {
    width: '80%',
    marginTop: 8,
    textAlign: 'center',
  },
})

class EditModal extends Component {
  // ========================================
  // STATE LOCAL
  // ========================================
  constructor(props) {
    super(props)
    this.state = {
      dragActive: false,
      uploadSubmitting: false,
      uploadStep: null, // 'register' | 'uploading' | 'uploaded' | 'queued' | 'processing' | 'done' | 'stalled'  (confirming removido — pipeline auto-trigger via S3)
      uploadMessage: '',
      cmsTituloLivro: '',
      cmsIsbnLivro: '',
      cmsAutoresLivro: [],
      cmsEspecialidadesLivro: [],
      cmsEdicaoLivro: '',
      cmsCodigoProtheus: '',
      cmsCopyrightLivro: '',
      cmsPlan: null,
      preRegistrationId: null,
      cmsMetadataLoading: false,
      cmsMetadataSaving: false,
      cmsMetadataError: null,
    }
  }

  componentDidUpdate(prevProps) {
    const prevBook = prevProps.bookVectorization || {}
    const currentBook = this.props.bookVectorization || {}

    if (
      this.props.open &&
      currentBook.id &&
      currentBook.isbn &&
      (!prevProps.open || prevBook.isbn !== currentBook.isbn)
    ) {
      this.loadCmsPlan(currentBook.isbn)
    }

    if (
      this.props.open &&
      !currentBook.id &&
      currentBook.isbn &&
      prevBook.isbn !== currentBook.isbn &&
      String(currentBook.isbn).replace(/\s/g, '').length >= 10
    ) {
      this.loadCmsPlan(currentBook.isbn)
    }

    if (this.props.open && !currentBook.id && !prevProps.open) {
      this.setState({
        cmsPlan: null,
        cmsTituloLivro: '',
        cmsIsbnLivro: '',
        cmsAutoresLivro: [],
        cmsEspecialidadesLivro: [],
        cmsEdicaoLivro: '',
        cmsCodigoProtheus: '',
        cmsCopyrightLivro: '',
        preRegistrationId: null,
        cmsMetadataLoading: false,
        cmsMetadataSaving: false,
        cmsMetadataError: null,
      })
    }
  }

  // ========================================
  // HANDLERS
  // ========================================
  getCmsBookData = () => {
    return {
      autoresLivro: this.state.cmsAutoresLivro,
      especialidadesLivro: this.state.cmsEspecialidadesLivro,
      edicaoLivro: this.state.cmsEdicaoLivro,
      codigoProtheus: this.state.cmsCodigoProtheus,
      copyrightLivro: this.state.cmsCopyrightLivro,
    }
  }

  getSelectLabels = (values) => {
    return formatContentSelectValue(values || [])
      .map((item) => item.label)
      .filter(Boolean)
  }

  createBookPreRegistration = (bookVectorization) => {
    const { preRegistrationId } = this.state

    if (preRegistrationId) {
      return Promise.resolve({ _id: preRegistrationId })
    }

    const cms = this.getCmsBookData()
    const data = createPreRegistrationFormData({
      tipo: 'livro',
      titulo: bookVectorization.book_name || '',
      isbn: bookVectorization.isbn || '',
      isbnVetorizacao: bookVectorization.isbn || '',
      autores: cms.autoresLivro || [],
      especialidades: cms.especialidadesLivro || [],
      codigo: cms.codigoProtheus || '',
      copyright: cms.copyrightLivro || '',
      edicao: cms.edicaoLivro || '',
    })

    return API.preRegistrations.addPreRegistration(data).then((response) => {
      const preRegistration = response.data || {}

      if (!preRegistration._id) {
        return Promise.reject(new Error('O CMS não retornou o identificador do pré-cadastro.'))
      }

      this.setState({ preRegistrationId: preRegistration._id })
      return preRegistration
    })
  }

  getSafeSelectAuthors = (term, callback) => {
    API.authors
      .searchAuthors(term)
      .then((response) => {
        callback(null, {
          options: formatContentSelectValue(response.data || []),
          complete: true,
        })
      })
      .catch(() => {
        callback(null, {
          options: [],
          complete: true,
        })
      })
  }

  getSafeSelectSpecialities = (term, callback) => {
    API.specialities
      .searchSpecialities(term)
      .then((response) => {
        callback(null, {
          options: formatContentSelectValue(response.data || []),
          complete: true,
        })
      })
      .catch(() => {
        callback(null, {
          options: [],
          complete: true,
        })
      })
  }

  applyCmsPreRegistration = (isbn, preRegistration, previousPlan) => {
    const cms = {
      autoresLivro: formatContentSelectValue(preRegistration.autores || []),
      especialidadesLivro: formatContentSelectValue(preRegistration.especialidades || []),
      edicaoLivro: preRegistration.edicao || '',
      codigoProtheus: preRegistration.codigo || '',
      copyrightLivro: preRegistration.copyright || '',
    }
    const plan = {
      ...(previousPlan || {}),
      enabled: true,
      cmsStatus: 'pre-registered',
      preRegistrationId: preRegistration._id,
      title: preRegistration.titulo || '',
      isbn: preRegistration.isbn || '',
      cms,
      source: 'cms-pre-registration',
    }

    saveBookCmsPlan(isbn, plan)
    this.setState({
      cmsPlan: plan,
      cmsTituloLivro: preRegistration.titulo || '',
      cmsIsbnLivro: preRegistration.isbn || '',
      cmsAutoresLivro: cms.autoresLivro,
      cmsEspecialidadesLivro: cms.especialidadesLivro,
      cmsEdicaoLivro: cms.edicaoLivro,
      cmsCodigoProtheus: cms.codigoProtheus,
      cmsCopyrightLivro: cms.copyrightLivro,
      preRegistrationId: preRegistration._id,
      cmsMetadataLoading: false,
      cmsMetadataError: null,
    })
  }

  loadCmsPlan = (isbn) => {
    const plan = getBookCmsPlan(isbn)
    const book = this.props.bookVectorization || {}
    this.cmsLookupIsbn = String(isbn)

    this.setState({
      cmsPlan: plan || null,
      cmsTituloLivro: (plan || {}).title || book.book_name || '',
      cmsIsbnLivro: (plan || {}).isbn || book.isbn || '',
      cmsAutoresLivro: (((plan || {}).cms || {}).autoresLivro || []),
      cmsEspecialidadesLivro: (((plan || {}).cms || {}).especialidadesLivro || []),
      cmsEdicaoLivro: (((plan || {}).cms || {}).edicaoLivro || ''),
      cmsCodigoProtheus: (((plan || {}).cms || {}).codigoProtheus || ''),
      cmsCopyrightLivro: (((plan || {}).cms || {}).copyrightLivro || ''),
      preRegistrationId: (plan || {}).preRegistrationId || null,
      cmsMetadataLoading: true,
      cmsMetadataError: null,
    })

    const request = plan && plan.preRegistrationId
      ? API.preRegistrations.getPreRegistration(plan.preRegistrationId)
      : API.preRegistrations.getBookPreRegistrationByIsbn(isbn)

    return request
      .catch((err) => {
        const notFound = err && err.response && err.response.status === 404
        if (notFound && plan && plan.preRegistrationId) {
          return API.preRegistrations.getBookPreRegistrationByIsbn(isbn)
        }
        return Promise.reject(err)
      })
      .then((response) => {
        if (this.cmsLookupIsbn !== String(isbn)) return null
        return this.applyCmsPreRegistration(isbn, response.data || {}, plan)
      })
      .catch((err) => {
        if (this.cmsLookupIsbn !== String(isbn)) return null
        const notFound = err && err.response && err.response.status === 404
        this.setState({
          cmsMetadataLoading: false,
          cmsMetadataError: notFound
            ? null
            : getApiErrorMessage(err, 'Não foi possível carregar o pré-cadastro do CMS.'),
        })
      })
  }

  handleSaveCmsMetadata = (e) => {
    e.preventDefault()

    const { bookVectorization, changeMessage } = this.props
    const { preRegistrationId, cmsTituloLivro, cmsIsbnLivro } = this.state
    const cms = this.getCmsBookData()
    const data = createPreRegistrationFormData({
      tipo: 'livro',
      titulo: cmsTituloLivro,
      isbn: cmsIsbnLivro,
      isbnVetorizacao: bookVectorization.isbn || '',
      autores: cms.autoresLivro || [],
      especialidades: cms.especialidadesLivro || [],
      codigo: cms.codigoProtheus || '',
      copyright: cms.copyrightLivro || '',
      edicao: cms.edicaoLivro || '',
    })

    this.setState({ cmsMetadataSaving: true, cmsMetadataError: null })

    const request = preRegistrationId
      ? API.preRegistrations.editPreRegistration(data, preRegistrationId)
      : API.preRegistrations.addPreRegistration(data)

    return request
      .then((response) => {
        const saved = response.data || {}
        const id = saved._id || preRegistrationId

        if (!id) {
          return Promise.reject(new Error('O CMS não retornou o identificador do pré-cadastro.'))
        }

        return API.preRegistrations.getPreRegistration(id).then((currentResponse) => {
          this.applyCmsPreRegistration(
            bookVectorization.isbn,
            currentResponse.data || saved,
            this.state.cmsPlan,
          )
          this.setState({ cmsMetadataSaving: false })
          changeMessage({
            message: 'Metadados do pré-cadastro atualizados no CMS e nos capítulos vinculados.',
          })
        })
      })
      .catch((err) => {
        const message = getApiErrorMessage(err, 'Não foi possível atualizar os metadados do CMS.')
        this.setState({ cmsMetadataSaving: false, cmsMetadataError: message })
        changeMessage({ message, type: 'error' })
      })
  }

  handleSave = (formData, bookVectorization) => {
    const { onClose, addBookVectorization, changeMessage } = this.props
    var self = this
    var preRegistration = null

    this.setState({
      uploadSubmitting: true,
      uploadStep: 'pre-register',
      uploadMessage: 'Criando o pré-cadastro do livro no CMS...',
    })

    formData.onProgress = function (info) {
      self.setState({ uploadStep: info.step, uploadMessage: info.message })
    }

    return this.createBookPreRegistration(bookVectorization)
      .then(function (createdPreRegistration) {
        preRegistration = createdPreRegistration

        saveBookCmsPlan(formData.isbn, {
          enabled: true,
          cmsStatus: 'pre-registered',
          preRegistrationId: createdPreRegistration._id,
          title: formData.titulo || '',
          isbn: formData.isbn || '',
          cms: formData.cms,
          source: 'book-vectorization-create',
          createdAt: new Date().toISOString(),
        })

        self.setState({
          uploadStep: 'register',
          uploadMessage: 'Pré-cadastro concluído. Iniciando o upload para vetorização...',
        })

        return addBookVectorization(formData)
      })
      .then(function (result) {
        var isbn = (result || {}).isbn || formData.isbn
        var uploadConfirmed = (result || {}).uploadConfirmed

        if (uploadConfirmed && isbn) {
          // Upload ao S3 concluído — pipeline será disparado automaticamente via S3 ObjectCreated event
          changeMessage({
            message:
              'Pré-cadastro criado e livro enviado para vetorização. Acompanhe o processamento e revise os capítulos quando ele finalizar.',
          })
          self.setState({
            uploadSubmitting: false,
            uploadStep: 'queued',
            uploadMessage: '',
          })
          onClose()
        } else {
          // Sem upload (registro sem arquivo)
          changeMessage({ message: 'Livro registrado.' })
          self.setState({ uploadSubmitting: false })
          onClose()
        }
      })
      .catch(function (err) {
        var errorMsg = preRegistration
          ? 'O pré-cadastro foi criado, mas não foi possível enviar o livro para vetorização.'
          : 'Não foi possível criar o pré-cadastro do livro no CMS.'

        errorMsg = getApiErrorMessage(err, errorMsg)

        self.setState({ uploadSubmitting: false, uploadStep: null })
        changeMessage({ message: errorMsg, type: 'error' })
      })
  }

  handleSubmit = (e, bookVectorization) => {
    e.preventDefault()
    const { setBookVectorizationFormTouched } = this.props
    const { uploadSubmitting } = this.state

    if (uploadSubmitting) return

    if (this.props.bookVectorizationFormErrors.valid) {
      var cms = this.getCmsBookData()
      var autores = this.getSelectLabels(cms.autoresLivro)
      var especialidades = this.getSelectLabels(cms.especialidadesLivro)
      var bookData = {
        titulo: bookVectorization.book_name || '',
        isbn: bookVectorization.isbn || '',
        cms,
      }

      if (bookVectorization.arquivo) {
        bookData.arquivo = bookVectorization.arquivo
      }

      if (autores.length > 0) {
        bookData.autores = autores
      }

      var copyrightYear = parseInt(cms.copyrightLivro, 10)
      if (!Number.isNaN(copyrightYear)) {
        bookData.ano = copyrightYear
      }

      if (especialidades.length > 0) {
        bookData.especialidade = especialidades[0]
      }

      this.handleSave(bookData, bookVectorization)
    } else {
      setBookVectorizationFormTouched()
    }
  }

  validateAndSetFile = (file) => {
    const { handleChangeBookVectorization, bookVectorization, changeMessage } = this.props

    if (!file) return

    var maxSize = 550 * 1024 * 1024

    if (file.size > maxSize) {
      var fileSizeMB = (file.size / (1024 * 1024)).toFixed(2)
      changeMessage({
        message: 'O arquivo possui ' + fileSizeMB + 'MB. O limite máximo permitido é de 550MB.',
        type: 'error',
      })
      return
    }

    var fileName = file.name.toLowerCase()
    var allowed = ALLOWED_EXTENSIONS.some(function (ext) { return fileName.endsWith(ext) }) ||
      ALLOWED_MIMES.some(function (mime) { return file.type === mime })

    if (!allowed) {
      changeMessage({
        message: 'Apenas arquivos ' + ACCEPTED_LABEL + ' são permitidos.',
        type: 'error',
      })
      return
    }

    handleChangeBookVectorization({
      ...bookVectorization,
      arquivo: file,
    })
  }

  handleFileChange = (e) => {
    this.validateAndSetFile(e.target.files[0])
    e.target.value = ''
  }

  handleRemoveFile = () => {
    const { handleChangeBookVectorization, bookVectorization } = this.props
    handleChangeBookVectorization({
      ...bookVectorization,
      arquivo: null,
    })
  }

  handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (this.isUploadDisabled()) return
    this.setState({ dragActive: true })
  }

  handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ dragActive: false })
  }

  handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ dragActive: false })

    if (this.isUploadDisabled()) return

    var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
    this.validateAndSetFile(file)
  }

  handleUploadKeyDown = (e) => {
    if (this.isUploadDisabled()) return

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (this.fileInput) {
        this.fileInput.click()
      }
    }
  }

  handleClose = () => {
    const { onClose } = this.props
    const { uploadSubmitting } = this.state

    if (!uploadSubmitting) {
      onClose()
    }
  }

  isUploadDisabled = () => {
    const { loadingBookVectorization, token } = this.props
    const { uploadSubmitting } = this.state

    return (
      readOnly('vetorizacaoLivros', token) ||
      loadingBookVectorization ||
      uploadSubmitting
    )
  }

  clearErrors = () => {
    const { setBookVectorizationFormTouched } = this.props
    setBookVectorizationFormTouched(false)
  }

  // ========================================
  // RENDER COMPONENTS
  // ========================================
  renderFileUpload = () => {
    const { classes, bookVectorization } = this.props
    const { dragActive } = this.state
    const { arquivo } = bookVectorization
    const isReadOnly = this.isUploadDisabled()
    const uploadAreaClassName =
      classes.uploadArea + (dragActive ? ' ' + classes.uploadAreaActive : '')

    if (arquivo && arquivo.name) {
      const fileSizeInMB = (arquivo.size / (1024 * 1024)).toFixed(2)
      return (
        <Fragment>
          <input
            id="arquivo-upload"
            type="file"
            accept={ACCEPT_ATTR}
            onChange={this.handleFileChange}
            className={classes.uploadInput}
            disabled={isReadOnly}
            ref={(input) => {
              this.fileInput = input
            }}
          />
          <div className={classes.fileInfo}>
            <InsertDriveFile style={{ color: '#1976d2' }} />
            <div className={classes.fileDetails}>
              <Typography variant="body2" noWrap title={arquivo.name}>
                {arquivo.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {fileSizeInMB} MB
              </Typography>
            </div>
          </div>
          {!isReadOnly ? (
            <div className={classes.fileActions}>
              <Button
                size="small"
                className={classes.fileActionButton}
                onClick={() => {
                  if (this.fileInput) {
                    this.fileInput.click()
                  }
                }}
              >
                Trocar arquivo
              </Button>
              <Button
                size="small"
                color="secondary"
                className={classes.fileActionButton}
                onClick={this.handleRemoveFile}
              >
                Remover arquivo
              </Button>
            </div>
          ) : null}
        </Fragment>
      )
    }

    return (
      <Fragment>
        <input
          id="arquivo-upload"
          type="file"
          accept={ACCEPT_ATTR}
          onChange={this.handleFileChange}
          className={classes.uploadInput}
          disabled={isReadOnly}
          ref={(input) => {
            this.fileInput = input
          }}
        />
        <label htmlFor="arquivo-upload">
          <div
            className={uploadAreaClassName}
            role="button"
            tabIndex={isReadOnly ? -1 : 0}
            aria-disabled={isReadOnly}
            aria-describedby="arquivo-upload-help"
            onKeyDown={this.handleUploadKeyDown}
            onDragOver={this.handleDragOver}
            onDragEnter={this.handleDragEnter}
            onDragLeave={this.handleDragLeave}
            onDrop={this.handleDrop}
          >
            <CloudUpload className={classes.uploadIcon} />
            <Typography variant="h6" gutterBottom>
              {dragActive
                ? 'Solte o arquivo para selecionar'
                : 'Clique ou arraste para selecionar ' + ACCEPTED_LABEL}
            </Typography>
            <Typography id="arquivo-upload-help" variant="caption" color="textSecondary">
              Tamanho máximo: 550MB
            </Typography>
          </div>
        </label>
      </Fragment>
    )
  }

  renderStatusBadge = (status) => {
    const { classes } = this.props
    const config = STATUS_CONFIG[status] || { label: status, color: 'default' }
    return <Chip label={config.label} color={config.color} className={classes.statusChip} />
  }

  getUploadProgressValue = () => {
    const { uploadStep } = this.state

    if (uploadStep === 'pre-register') return 5
    if (uploadStep === 'register') return 10
    if (uploadStep === 'uploaded') return 70
    if (uploadStep === 'processing') return 88
    if (uploadStep === 'stalled') return 60

    return 0
  }

  renderCmsMetadataFields = (isReadOnly, options) => {
    const { classes } = this.props
    options = options || {}
    const {
      cmsAutoresLivro,
      cmsEspecialidadesLivro,
      cmsEdicaoLivro,
      cmsCodigoProtheus,
      cmsCopyrightLivro,
      uploadSubmitting,
      cmsMetadataLoading,
      cmsMetadataSaving,
      cmsMetadataError,
    } = this.state

    const disabled = isReadOnly || uploadSubmitting || cmsMetadataLoading || cmsMetadataSaving

    return (
      <fieldset className={classes.fieldset}>
        <legend className={classes.legend}>
          {options.legend || 'Pré-cadastro do livro no CMS'}
        </legend>

        {cmsMetadataLoading ? (
          <Typography variant="caption" color="textSecondary">
            Carregando os metadados atuais do CMS...
          </Typography>
        ) : null}

        {cmsMetadataError ? (
          <Typography variant="caption" color="error">
            {cmsMetadataError}
          </Typography>
        ) : null}

        <div className={classes.formControl}>
          <TextField
            disabled={disabled}
            fullWidth
            onChange={(obj) => {
              this.setState({ cmsAutoresLivro: obj || [] })
            }}
            value={formatContentSelectValue(cmsAutoresLivro || [])}
            placeholder="Selecione..."
            name="book-vectorization-cms-autores-livro"
            label="Autores (opcional)"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputComponent: SelectWrapped,
              inputProps: {
                multi: true,
                filterOptions: filterOptionsFunction,
                instanceId: 'book-vectorization-cms-autores-livro',
                id: 'book-vectorization-cms-autores-livro',
                loadOptions: this.getSafeSelectAuthors,
                disableDelete: disabled,
              },
            }}
          />
        </div>

        <div className={classes.formControl}>
          <TextField
            disabled={disabled}
            fullWidth
            onChange={(obj) => {
              this.setState({ cmsEspecialidadesLivro: obj || [] })
            }}
            value={formatContentSelectValue(cmsEspecialidadesLivro || [])}
            placeholder="Selecione..."
            name="book-vectorization-cms-especialidades-livro"
            label="Especialidades (opcional)"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputComponent: SelectWrapped,
              inputProps: {
                multi: true,
                filterOptions: filterOptionsFunction,
                instanceId: 'book-vectorization-cms-especialidades-livro',
                id: 'book-vectorization-cms-especialidades-livro',
                loadOptions: this.getSafeSelectSpecialities,
                disableDelete: disabled,
              },
            }}
          />
        </div>

        <FormControl className={classes.formControl} fullWidth>
          <InputLabel htmlFor="book-vectorization-cms-codigo-protheus">Código Protheus (opcional)</InputLabel>
          <Input
            disabled={disabled}
            fullWidth
            type="text"
            onChange={(e) => this.setState({ cmsCodigoProtheus: e.target.value })}
            value={cmsCodigoProtheus}
            id="book-vectorization-cms-codigo-protheus"
          />
        </FormControl>

        <FormControl className={classes.formControl} fullWidth>
          <InputLabel htmlFor="book-vectorization-cms-edicao-livro">Edição (opcional)</InputLabel>
          <Input
            disabled={disabled}
            fullWidth
            type="text"
            onChange={(e) => this.setState({ cmsEdicaoLivro: e.target.value })}
            value={cmsEdicaoLivro}
            id="book-vectorization-cms-edicao-livro"
          />
        </FormControl>

        <FormControl className={classes.formControl} fullWidth>
          <InputLabel htmlFor="book-vectorization-cms-copyright-livro">Ano copyright (opcional)</InputLabel>
          <Input
            disabled={disabled}
            fullWidth
            type="number"
            onChange={(e) => this.setState({ cmsCopyrightLivro: e.target.value })}
            value={cmsCopyrightLivro}
            id="book-vectorization-cms-copyright-livro"
            inputProps={{ min: 1000, max: new Date().getFullYear() + 1 }}
          />
        </FormControl>
      </fieldset>
    )
  }

  // ========================================
  // RENDER PRINCIPAL
  // ========================================
  render() {
    const {
      fullScreen,
      open = false,
      classes,
      bookVectorization,
      handleChangeBookVectorization,
      loadingBookVectorization,
      bookVectorizationFormErrors,
      formTouched,
      token,
    } = this.props

    const {
      uploadSubmitting,
      uploadStep,
      uploadMessage,
      cmsTituloLivro,
      cmsIsbnLivro,
      cmsMetadataLoading,
      cmsMetadataSaving,
    } = this.state
    const { handleSubmit, clearErrors, handleClose } = this

    const isPermissionReadOnly = readOnly('vetorizacaoLivros', token)
    const isReadOnly = isPermissionReadOnly || uploadSubmitting || cmsMetadataSaving
    const modalId = bookVectorization.id
      ? 'vetorizacao-livros-metadados-livro'
      : 'vetorizacao-livros-novo-livro'
    const modalTitle = bookVectorization.id
      ? 'Vetorização de livros — Metadados do livro: ' +
        (cmsTituloLivro || bookVectorization.book_name || bookVectorization.isbn)
      : 'Vetorização de livros — Novo livro'
    const isLoading = loadingBookVectorization || uploadSubmitting

    return (
      <div>
        <Dialog
          modalId={modalId}
          modalParams={{
            isbn: bookVectorization.isbn,
            registro: bookVectorization.id,
          }}
          fullScreen={fullScreen}
          fullWidth
          maxWidth="sm"
          open={open}
          onClose={handleClose}
          className={classes.dialog}
        >
          {/* TÍTULO - Sempre visível */}
          <DialogTitle id={modalId + '-title'}>{modalTitle}</DialogTitle>

          {/* CONTEÚDO - Sempre visível, mas com overlay se carregando */}
          <DialogContent className={classes.dialogContent}>
            {isLoading ? (
              <div className={classes.loadingOverlay} aria-live="polite" aria-busy="true">
                <CircularProgress />
                <Typography variant="body2" style={{ marginTop: '16px', fontWeight: 500 }}>
                  {uploadSubmitting
                    ? uploadMessage || 'Enviando livro para vetorização...'
                    : 'Carregando livro...'}
                </Typography>
                {uploadStep && uploadSubmitting && (
                  <div style={{ width: '80%', marginTop: 12 }}>
                    <LinearProgress
                      variant={uploadStep === 'uploading' ? 'indeterminate' : 'determinate'}
                      value={this.getUploadProgressValue()}
                      color={uploadStep === 'stalled' ? 'secondary' : 'primary'}
                      style={{ height: 6, borderRadius: 3 }}
                    />
                    {uploadStep === 'stalled' && (
                      <div style={{ textAlign: 'center', marginTop: 12 }}>
                        <Typography variant="caption" color="error" style={{ display: 'block', marginBottom: 8 }}>
                          O pipeline não avançou após o upload. O arquivo pode estar no S3 mas a Lambda não foi acionada.
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={() => {
                            this.setState({ uploadSubmitting: false, uploadStep: null })
                            this.props.onClose()
                          }}
                        >
                          Fechar e verificar no painel de Status
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : null}
            <form
              id={'book-vectorization-form'}
              noValidate
              onSubmit={(e) => {
                if (bookVectorization.id) {
                  this.handleSaveCmsMetadata(e)
                } else {
                  handleSubmit(e, bookVectorization)
                }
              }}
            >
              {!bookVectorization.id ? <BookVectorizationFlow compact /> : null}

              {/* Nome do Livro */}
              <FormControl
                error={
                  !((bookVectorizationFormErrors.fields || {})['book_name'] || {}).valid &&
                  formTouched
                }
                className={classes.formControl}
                fullWidth
              >
                <InputLabel htmlFor="field-book-name">
                  {bookVectorization.id ? 'Nome do Livro no CMS' : 'Nome do Livro *'}
                </InputLabel>
                <Input
                  disabled={isReadOnly || cmsMetadataLoading}
                  fullWidth
                  required={!bookVectorization.id}
                  type="text"
                  onChange={(e) => {
                    if (bookVectorization.id) {
                      this.setState({ cmsTituloLivro: e.target.value })
                    } else {
                      clearErrors()
                      handleChangeBookVectorization({
                        ...bookVectorization,
                        book_name: e.target.value,
                      })
                    }
                  }}
                  value={bookVectorization.id ? cmsTituloLivro : bookVectorization.book_name || ''}
                  autoFocus={!bookVectorization.id}
                  id="field-book-name"
                />
                {!((bookVectorizationFormErrors.fields || {})['book_name'] || {}).valid &&
                formTouched ? (
                  <Typography className={classes.errorMessage} noWrap color={'error'}>
                    {((bookVectorizationFormErrors.fields || {})['book_name'] || {}).errorMessage}
                  </Typography>
                ) : null}
              </FormControl>

              {/* ISBN */}
              <FormControl
                error={
                  !((bookVectorizationFormErrors.fields || {})['isbn'] || {}).valid && formTouched
                }
                className={classes.formControl}
                fullWidth
              >
                <InputLabel htmlFor="field-isbn">
                  {bookVectorization.id ? 'ISBN editorial no CMS' : 'ISBN *'}
                </InputLabel>
                <Input
                  disabled={isReadOnly || cmsMetadataLoading}
                  fullWidth
                  required={!bookVectorization.id}
                  type="text"
                  onChange={(e) => {
                    if (bookVectorization.id) {
                      this.setState({ cmsIsbnLivro: e.target.value })
                    } else {
                      clearErrors()
                      handleChangeBookVectorization({
                        ...bookVectorization,
                        isbn: e.target.value,
                      })
                    }
                  }}
                  value={bookVectorization.id ? cmsIsbnLivro : bookVectorization.isbn || ''}
                  id="field-isbn"
                />
                {!((bookVectorizationFormErrors.fields || {})['isbn'] || {}).valid &&
                formTouched ? (
                  <Typography className={classes.errorMessage} noWrap color={'error'}>
                    {((bookVectorizationFormErrors.fields || {})['isbn'] || {}).errorMessage}
                  </Typography>
                ) : null}
              </FormControl>

              {!isPermissionReadOnly ? (
                <Fragment>
                  <Typography variant="body2" color="textSecondary">
                    {bookVectorization.id
                      ? 'Atualize somente os metadados disponíveis. A alteração será refletida nos capítulos já vinculados e nos próximos cadastros.'
                      : 'Informe somente os metadados disponíveis. Autores, especialidades, código, edição e ano não impedem o pré-cadastro quando estiverem em branco.'}
                  </Typography>
                  {bookVectorization.id ? (
                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 6 }}>
                      Identificador técnico da vetorização: {bookVectorization.isbn}
                    </Typography>
                  ) : null}
                  {!bookVectorization.id ? (
                    <div className={classes.warningBox}>
                      <Typography variant="body2">
                        Este envio cria primeiro o pré-cadastro do livro no CMS e depois envia ao
                        Sniper somente os metadados técnicos da vetorização. Autores e
                        especialidades são seleções do Mongo; cadastre esses itens nas abas
                        próprias antes de selecioná-los aqui.
                      </Typography>
                    </div>
                  ) : null}
                  {this.renderCmsMetadataFields(isReadOnly, {
                    legend: bookVectorization.id
                      ? 'Metadados editáveis do pré-cadastro no CMS'
                      : 'Pré-cadastro do livro no CMS',
                  })}
                </Fragment>
              ) : null}

              {/* Arquivo */}
              {!isPermissionReadOnly && !bookVectorization.id ? (
                <FormControl
                  error={
                    !((bookVectorizationFormErrors.fields || {})['arquivo'] || {}).valid &&
                    formTouched
                  }
                  className={classes.formControl}
                  fullWidth
                >
                  <InputLabel shrink htmlFor="arquivo-upload">
                    {'Arquivo (' + ACCEPTED_LABEL + ') *'}
                  </InputLabel>
                  <div style={{ marginTop: '24px' }}>{this.renderFileUpload()}</div>
                  {!((bookVectorizationFormErrors.fields || {})['arquivo'] || {}).valid &&
                  formTouched ? (
                    <Typography className={classes.errorMessage} noWrap color={'error'}>
                      {((bookVectorizationFormErrors.fields || {})['arquivo'] || {}).errorMessage}
                    </Typography>
                  ) : null}
                </FormControl>
              ) : null}

              {/* ReadOnly Info */}
              {bookVectorization.id ? (
                <Fragment>
                  {bookVectorization.processamento &&
                  bookVectorization.processamento.status_atual ? (
                    <FormControl className={classes.formControl} fullWidth>
                      <InputLabel shrink>Status</InputLabel>
                      <div style={{ marginTop: '24px' }}>
                        {this.renderStatusBadge(bookVectorization.processamento.status_atual)}
                      </div>
                    </FormControl>
                  ) : null}

                  {bookVectorization.processamento && bookVectorization.processamento.ciclo_uuid ? (
                    <FormControl className={classes.formControl} fullWidth>
                      <InputLabel shrink htmlFor="field-ciclo-uuid">
                        ID do Processamento
                      </InputLabel>
                      <Input
                        disabled
                        fullWidth
                        value={bookVectorization.processamento.ciclo_uuid}
                        id="field-ciclo-uuid"
                      />
                    </FormControl>
                  ) : null}

                  {bookVectorization.created_at ? (
                    <FormControl className={classes.formControl} fullWidth>
                      <InputLabel shrink htmlFor="field-created-at">
                        Data de Upload
                      </InputLabel>
                      <Input
                        disabled
                        fullWidth
                        value={new Date(bookVectorization.created_at).toLocaleString('pt-BR')}
                        id="field-created-at"
                      />
                    </FormControl>
                  ) : null}
                </Fragment>
              ) : null}
            </form>
          </DialogContent>

          {/* AÇÕES - Desabilitadas durante o loading */}
          <DialogActions>
            <Button onClick={handleClose} color="default" disabled={uploadSubmitting}>
              {bookVectorization.id ? 'Fechar' : 'Cancelar'}
            </Button>

            {!bookVectorization.id && !readOnly('vetorizacaoLivros', token) ? (
              <Button
                type="submit"
                form="book-vectorization-form"
                variant="contained"
                color="primary"
                disabled={uploadSubmitting}
              >
                {uploadSubmitting ? 'Enviando...' : 'Criar pré-cadastro e vetorizar'}
              </Button>
            ) : null}

            {bookVectorization.id && !isPermissionReadOnly ? (
              <Button
                type="submit"
                form="book-vectorization-form"
                variant="contained"
                color="primary"
                disabled={cmsMetadataLoading || cmsMetadataSaving}
              >
                {cmsMetadataSaving ? 'Salvando...' : 'Salvar metadados do CMS'}
              </Button>
            ) : null}
          </DialogActions>
        </Dialog>
      </div>
    )
  }
}

EditModal.propTypes = {
  fullScreen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  classes: PropTypes.object.isRequired,
  bookVectorization: PropTypes.object.isRequired,
  handleChangeBookVectorization: PropTypes.func.isRequired,
  loadingBookVectorization: PropTypes.bool.isRequired,
  bookVectorizationFormErrors: PropTypes.object.isRequired,
  formTouched: PropTypes.bool.isRequired,
  addBookVectorization: PropTypes.func.isRequired,
  setBookVectorizationFormTouched: PropTypes.func.isRequired,
  changeMessage: PropTypes.func.isRequired,
  token: PropTypes.string,
}

const mapStateToProps = ({ bookVectorization, auth }) => {
  const {
    bookVectorization: bookVectorizationData,
    loadingBookVectorization,
    bookVectorizationFormErrors,
    bookVectorizationFormTouched,
  } = bookVectorization
  const { token } = auth

  return {
    bookVectorization: bookVectorizationData,
    loadingBookVectorization,
    bookVectorizationFormErrors,
    formTouched: bookVectorizationFormTouched,
    token,
  }
}

const mapDispatchToProps = {
  handleChangeBookVectorization: bookVectorizationActions.handleChangeBookVectorization,
  addBookVectorization: bookVectorizationActions.addBookVectorization,
  setBookVectorizationFormTouched: bookVectorizationActions.setBookVectorizationFormTouched,
  changeMessage: messageActions.changeMessage,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withMobileDialog()(withStyles(styles)(EditModal)))



// WEBPACK FOOTER //
// ./src/containers/BookVectorization/EditModal.js